"use client"

import { useRef, useState, useEffect, useCallback } from "react"
import type { PCBStore } from "@/hooks/use-pcb-store"
import type { Tool, LineStyle, ProjectProperties } from "@/lib/pcb-types"
import { PRESET_COLORS } from "@/lib/pcb-types"
import { exportVectorPDF } from "@/lib/pdf-vector-export"
import {
  MousePointer,
  Spline,
  Pentagon,
  Square,
  Circle,
  Pencil,
  Type,
  Target,
  Ruler,
  Eraser,
  ImagePlus,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Download,
  Lock,
  FileUp,
  FileDown,
  Printer,
  Undo2,
  Redo2,
  FilePlus,
  Settings2,
  FileText,
  ChevronDown,
  SlidersHorizontal,
} from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Slider } from "@/components/ui/slider"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface TopToolbarProps {
  store: PCBStore
}

interface ToolDef {
  id: Tool
  label: string
  icon: React.ComponentType<{ className?: string }>
  shortcut: string
}

// Custom probe icon component (inclined multimeter probe)
function ProbeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Probe tip (gold) */}
      <path d="M4 20L7 14" strokeWidth="2" />
      {/* Probe body (inclined) */}
      <rect x="6" y="10" width="4" height="8" rx="1" transform="rotate(-45 8 14)" fill="currentColor" fillOpacity="0.3" />
      {/* Handle grip lines */}
      <path d="M9 11L11 9M10 12L12 10M11 13L13 11" strokeWidth="1" opacity="0.5" />
    </svg>
  )
}

const tools: ToolDef[] = [
  { id: "select", label: "Select", icon: MousePointer, shortcut: "V" },
  { id: "polyline", label: "Polyline", icon: Spline, shortcut: "K" },
  { id: "polygon", label: "Polygon", icon: Pentagon, shortcut: "G" },
  { id: "rect", label: "Rectangle", icon: Square, shortcut: "R" },
  { id: "circle", label: "Circle", icon: Circle, shortcut: "C" },
  { id: "freehand", label: "Freehand", icon: Pencil, shortcut: "P" },
  { id: "text", label: "Text", icon: Type, shortcut: "T" },
  { id: "marker", label: "Marker", icon: Target, shortcut: "A" },
  { id: "probe", label: "Probe", icon: ProbeIcon, shortcut: "B" },
  { id: "measure", label: "Measure", icon: Ruler, shortcut: "M" },
  { id: "eraser", label: "Eraser", icon: Eraser, shortcut: "E" },
]

const LINE_STYLES: { id: LineStyle; label: string; preview: number[] }[] = [
  { id: "solid", label: "Solid", preview: [] },
  { id: "dotted", label: "Dotted", preview: [2, 3] },
  { id: "dashed", label: "Dashed", preview: [6, 3] },
  { id: "dashdot", label: "Dash-Dot", preview: [6, 3, 2, 3] },
]

export function TopToolbar({ store }: TopToolbarProps) {
  const imageInputRef = useRef<HTMLInputElement>(null)
  const jsonInputRef = useRef<HTMLInputElement>(null)
  const [customColor, setCustomColor] = useState(store.activeColor)
  const [showProjectProps, setShowProjectProps] = useState(false)
  const [projProps, setProjProps] = useState<ProjectProperties>({ ...store.projectProperties })
  const [showPreferences, setShowPreferences] = useState(false)
  const [prefColors, setPrefColors] = useState<string[]>([...PRESET_COLORS])
  const [prefMaxTextSize, setPrefMaxTextSize] = useState(72)

  const isToolDisabled =
    !store.activeLayer || store.activeLayer.locked || !store.activeLayer.visible

  const handleImportImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => { store.setPcbImage(ev.target?.result as string) }
    reader.readAsDataURL(file)
    e.target.value = ""
  }

  const handleExportPNG = () => {
    const canvas = document.querySelector("canvas")
    if (!canvas) return
    const link = document.createElement("a")
    link.download = store.getExportFileName("png")
    link.href = canvas.toDataURL("image/png")
    link.click()
  }

  // Print visible area directly
  const handlePrintDirect = useCallback(() => {
    const canvas = document.querySelector("canvas") as HTMLCanvasElement | null
    if (!canvas) { alert("No canvas found. Load an image first."); return }

    const dataUrl = canvas.toDataURL("image/png")
    
    const iframe = document.createElement("iframe")
    iframe.style.position = "fixed"
    iframe.style.width = "0"
    iframe.style.height = "0"
    iframe.style.border = "none"
    iframe.style.left = "-9999px"
    document.body.appendChild(iframe)

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
    if (!iframeDoc) {
      const link = document.createElement("a")
      link.download = "pcb-print.png"
      link.href = dataUrl
      link.click()
      document.body.removeChild(iframe)
      return
    }

    iframeDoc.open()
    iframeDoc.write(`
      <html><head><title>PCB Print</title>
      <style>
        body { margin: 0; display: flex; align-items: center; justify-content: center; }
        img { max-width: 100%; max-height: 100vh; }
        @media print { body { margin: 0; } img { max-width: 100%; page-break-inside: avoid; } }
      </style></head><body>
      <img src="${dataUrl}" />
      </body></html>
    `)
    iframeDoc.close()

    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.print()
        setTimeout(() => { document.body.removeChild(iframe) }, 1000)
      }, 300)
    }
  }, [])

  // Export PDF (A4, landscape or portrait based on canvas aspect ratio) with vector graphics
  const handleExportPDF = useCallback(async () => {
    const canvas = document.querySelector("canvas") as HTMLCanvasElement | null
    if (!canvas) { alert("No canvas found. Load an image first."); return }

    try {
      // Export using vector PDF with jsPDF
      await exportVectorPDF({
        layers: store.layers,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        pcbImage: store.pcbImage,
        imageGrayscale: store.imageGrayscale,
        imageOverlay: store.imageOverlay,
        imageOverlayOpacity: store.imageOverlayOpacity,
        fileName: store.getExportFileName("pdf"),
      })
    } catch (e) {
      console.error("PDF export failed:", e)
      alert("PDF export failed. Please try again.")
    }
  }, [store])

  const handleExportJSON = () => {
    const jsonStr = store.exportJSON()
    const blob = new Blob([jsonStr], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.download = store.getExportFileName("json")
    link.href = url
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleImportJSON = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      const success = store.importJSON(text)
      if (!success) { alert("Invalid JSON project file") }
    }
    reader.readAsText(file)
    e.target.value = ""
  }

  const handleNewProject = () => {
    const hasContent = store.layers.some(l => l.objects.length > 0) || store.pcbImage
    if (hasContent) {
      const save = window.confirm("Do you want to save the current project before creating a new one?\n\nClick OK to export, or Cancel to discard.")
      if (save) {
        handleExportJSON()
      }
    }
    store.newProject()
  }

  const handleResetView = () => {
    const container = document.getElementById("pcb-canvas-container")
    const canvas = document.querySelector("canvas")
    if (!container || !canvas) {
      store.setZoom(1); store.setPanOffset({ x: 0, y: 0 }); return
    }
    
    // Calculate bounds including image and all objects
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    
    // Include image bounds if available
    const imgW = canvas.getAttribute("data-img-width")
    const imgH = canvas.getAttribute("data-img-height")
    if (imgW && imgH) {
      minX = Math.min(minX, 0)
      minY = Math.min(minY, 0)
      maxX = Math.max(maxX, parseInt(imgW))
      maxY = Math.max(maxY, parseInt(imgH))
    }
    
    // Include all object bounds
    for (const layer of store.layers) {
      for (const obj of layer.objects) {
        for (const pt of obj.points) {
          minX = Math.min(minX, pt.x)
          minY = Math.min(minY, pt.y)
          maxX = Math.max(maxX, pt.x)
          maxY = Math.max(maxY, pt.y)
        }
        // For circles, extend by radius
        if (obj.type === "circle" && obj.points.length >= 2) {
          const cx = obj.points[0].x, cy = obj.points[0].y
          const r = Math.hypot(obj.points[1].x - cx, obj.points[1].y - cy)
          minX = Math.min(minX, cx - r)
          minY = Math.min(minY, cy - r)
          maxX = Math.max(maxX, cx + r)
          maxY = Math.max(maxY, cy + r)
        }
      }
    }
    
    // If we have valid bounds, fit to them
    if (minX !== Infinity && maxX !== -Infinity) {
      const contentW = maxX - minX
      const contentH = maxY - minY
      const padding = 40
      const availW = container.clientWidth - padding * 2
      const availH = container.clientHeight - padding * 2
      const scaleX = availW / contentW
      const scaleY = availH / contentH
      const newZoom = Math.max(0.1, Math.min(10, Math.min(scaleX, scaleY)))
      const cx = (container.clientWidth - contentW * newZoom) / 2 - minX * newZoom
      const cy = (container.clientHeight - contentH * newZoom) / 2 - minY * newZoom
      store.setZoom(newZoom)
      store.setPanOffset({ x: cx, y: cy })
    } else {
      store.setZoom(1); store.setPanOffset({ x: 0, y: 0 })
    }
  }

  const handleSaveProjectProps = () => {
    store.setProjectProperties(projProps)
    setShowProjectProps(false)
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key === "Escape") { store.setActiveTool("select"); return }
      const key = e.key.toUpperCase()
      const tool = tools.find((t) => t.shortcut === key)
      if (tool) store.setActiveTool(tool.id)
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [store])

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-1 border-b border-border bg-toolbar-bg px-2 py-1.5">
        {/* PCB RE dropdown menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 pr-3 mr-1 border-r border-toolbar-border rounded px-1.5 py-1 hover:bg-tool-hover transition-colors">
              <div className="h-5 w-5 rounded bg-primary flex items-center justify-center">
                <svg className="h-3 w-3 text-primary-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
              </div>
              <span className="text-xs font-semibold tracking-wide text-foreground hidden md:inline">PCB RE</span>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52 bg-popover">
            <DropdownMenuItem onClick={handleNewProject}>
              <FilePlus className="mr-2 h-4 w-4" />
              New Project
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setProjProps({ ...store.projectProperties }); setShowProjectProps(true) }}>
              <Settings2 className="mr-2 h-4 w-4" />
              Project Properties
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowPreferences(true)}>
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              Preferences
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => imageInputRef.current?.click()}>
              <ImagePlus className="mr-2 h-4 w-4" />
              Import Image
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => jsonInputRef.current?.click()}>
              <FileUp className="mr-2 h-4 w-4" />
              Import JSON
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleExportJSON}>
              <FileDown className="mr-2 h-4 w-4" />
              Export JSON
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportPNG}>
              <Download className="mr-2 h-4 w-4" />
              Export PNG
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportPDF}>
              <FileText className="mr-2 h-4 w-4" />
              Export PDF (A4)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handlePrintDirect}>
              <Printer className="mr-2 h-4 w-4" />
              Print
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImportImage} className="hidden" />
        <input ref={jsonInputRef} type="file" accept=".json,application/json" onChange={handleImportJSON} className="hidden" />

        {/* Undo / Redo */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={() => store.undo()} disabled={!store.canUndo}
              className={`rounded p-1.5 transition-colors ${store.canUndo ? "text-muted-foreground hover:bg-tool-hover hover:text-foreground" : "text-muted-foreground/30 cursor-not-allowed"}`}>
              <Undo2 className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Undo (Ctrl+Z)</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={() => store.redo()} disabled={!store.canRedo}
              className={`rounded p-1.5 transition-colors ${store.canRedo ? "text-muted-foreground hover:bg-tool-hover hover:text-foreground" : "text-muted-foreground/30 cursor-not-allowed"}`}>
              <Redo2 className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Redo (Ctrl+Y)</TooltipContent>
        </Tooltip>

        <div className="mx-1 h-5 w-px bg-toolbar-border" />

        {/* Drawing Tools */}
        <div className="flex items-center gap-0.5">
          {tools.map((tool) => {
            const isActive = store.activeTool === tool.id
            const Icon = tool.icon
            const disabled = tool.id !== "select" && isToolDisabled
            return (
              <Tooltip key={tool.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => !disabled && store.setActiveTool(tool.id)}
                    disabled={disabled}
                    className={`relative rounded p-1.5 transition-all ${
                      isActive ? "bg-tool-active text-primary-foreground shadow-sm"
                        : disabled ? "text-muted-foreground/30 cursor-not-allowed"
                        : "text-muted-foreground hover:bg-tool-hover hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {isActive && <div className="absolute -bottom-0.5 left-1/2 h-0.5 w-3 -translate-x-1/2 rounded-full bg-primary-foreground" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex items-center gap-2">
                    <span>{tool.label}</span>
                    <kbd className="rounded bg-secondary px-1 py-0.5 text-[10px] font-mono text-muted-foreground">{tool.shortcut}</kbd>
                  </div>
                  {disabled && <p className="text-[10px] text-destructive mt-1">Layer is locked or hidden</p>}
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>

        <div className="mx-1 h-5 w-px bg-toolbar-border" />

        {/* Color Palette */}
        <Popover>
          <PopoverTrigger asChild>
            <button disabled={isToolDisabled} className={`flex items-center gap-1.5 rounded px-1.5 py-1 transition-colors ${isToolDisabled ? "opacity-30 cursor-not-allowed" : "hover:bg-tool-hover"}`}>
              <div className="h-4 w-4 rounded-sm ring-1 ring-border" style={{ backgroundColor: store.activeColor }} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-52 bg-popover p-3" align="start">
            <div className="space-y-3">
              <div className="space-y-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Preset Colors</span>
                <div className="grid grid-cols-5 gap-1.5">
                  {PRESET_COLORS.map((color) => (
                    <button key={color} onClick={() => store.setActiveColor(color)}
                      className={`h-6 w-6 rounded ring-1 transition-all ${store.activeColor === color ? "ring-foreground scale-110" : "ring-border hover:ring-muted-foreground"}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Custom</span>
                <div className="flex items-center gap-2">
                  <input type="color" value={customColor} onChange={(e) => { setCustomColor(e.target.value); store.setActiveColor(e.target.value) }} className="h-7 w-7 cursor-pointer rounded border-0 bg-transparent p-0" />
                  <input type="text" value={store.activeColor} onChange={(e) => { store.setActiveColor(e.target.value); setCustomColor(e.target.value) }} className="flex-1 rounded bg-secondary px-2 py-1 text-xs font-mono text-foreground outline-none border border-border focus:border-primary" />
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Stroke Width */}
        <div className={`flex items-center gap-1.5 ${isToolDisabled ? "opacity-30 pointer-events-none" : ""}`}>
          <span className="text-[10px] font-mono text-muted-foreground hidden lg:inline">W</span>
          <Slider min={1} max={10} step={1} value={[store.strokeWidth]} onValueChange={([v]) => store.setStrokeWidth(v)} className="w-14" />
          <span className="text-[10px] font-mono text-muted-foreground w-3">{store.strokeWidth}</span>
        </div>

        <div className="mx-1 h-5 w-px bg-toolbar-border" />

        {/* Line Style Picker */}
        <Popover>
          <PopoverTrigger asChild>
            <button disabled={isToolDisabled} className={`flex items-center gap-1 rounded px-1.5 py-1 transition-colors ${isToolDisabled ? "opacity-30 cursor-not-allowed" : "hover:bg-tool-hover"}`}>
              <svg width="24" height="14" viewBox="0 0 24 14" className="text-muted-foreground">
                <line x1="2" y1="7" x2="22" y2="7" stroke="currentColor" strokeWidth="2"
                  strokeDasharray={store.activeLineStyle === "solid" ? "none" : store.activeLineStyle === "dotted" ? "2 3" : store.activeLineStyle === "dashed" ? "6 3" : "6 3 2 3"}
                />
              </svg>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-36 bg-popover p-2" align="start">
            <div className="space-y-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Line Style</span>
              {LINE_STYLES.map((ls) => (
                <button key={ls.id} onClick={() => store.setActiveLineStyle(ls.id)}
                  className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs transition-colors ${store.activeLineStyle === ls.id ? "bg-tool-active text-primary-foreground" : "text-muted-foreground hover:bg-tool-hover hover:text-foreground"}`}>
                  <svg width="32" height="10" viewBox="0 0 32 10">
                    <line x1="0" y1="5" x2="32" y2="5" stroke="currentColor" strokeWidth="2" strokeDasharray={ls.preview.join(" ") || "none"} />
                  </svg>
                  <span>{ls.label}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <div className="mx-1 h-5 w-px bg-toolbar-border" />

        {/* Grayscale & Tint Control */}
        <Popover>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-1 rounded px-1.5 py-1 text-muted-foreground transition-colors hover:bg-tool-hover hover:text-foreground">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a10 10 0 0 1 0 20z" fill="currentColor" />
              </svg>
              <span className="text-[10px] font-mono hidden lg:inline">{store.imageGrayscale > 0 || store.imageTint > 0 ? "Adj" : "Img"}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-52 bg-popover p-3" align="start">
            <div className="space-y-3">
              <div className="space-y-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Grayscale</span>
                <Slider min={0} max={100} step={5} value={[store.imageGrayscale]} onValueChange={([v]) => store.setImageGrayscale(v)} />
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground">Color</span>
                  <span className="text-[10px] font-mono text-foreground">{store.imageGrayscale}%</span>
                  <span className="text-[10px] text-muted-foreground">Gray</span>
                </div>
              </div>
              <div className="space-y-2 pt-2 border-t border-border">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Colorize (Hue Shift)</span>
                <Slider min={0} max={360} step={1} value={[store.imageTint]} onValueChange={([v]) => store.setImageTint(v)} />
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground">0</span>
                  <span className="text-[10px] font-mono text-foreground">{store.imageTint}deg</span>
                  <span className="text-[10px] text-muted-foreground">360</span>
                </div>
              </div>
              <div className="space-y-2 pt-2 border-t border-border">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Color Tint (Overlay)</span>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={store.imageOverlay || "#000000"}
                    onChange={(e) => store.setImageOverlay(e.target.value)}
                    className="w-7 h-7 rounded cursor-pointer border border-border"
                  />
                  <Slider min={0} max={100} step={5} value={[store.imageOverlayOpacity]} onValueChange={([v]) => store.setImageOverlayOpacity(v)} className="flex-1" />
                  <span className="text-[10px] font-mono text-foreground w-8 text-right">{store.imageOverlayOpacity}%</span>
                </div>
              </div>
              {/* Magic preset button */}
              <div className="pt-2 border-t border-border">
                <button
                  onClick={() => {
                    store.setImageGrayscale(100)
                    store.setImageOverlay("#141ebe")
                    store.setImageOverlayOpacity(65)
                  }}
                  className="w-full flex items-center justify-center gap-1.5 rounded bg-primary/10 px-3 py-1.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
                >
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                    <path d="M9 18h6" />
                    <path d="M10 22h4" />
                  </svg>
                  Magic PCB View
                </button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Active layer indicator */}
        {store.activeLayer && (
          <div className="flex items-center gap-1.5 rounded bg-secondary px-2 py-1 mr-2">
            <div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: store.activeLayer.color }} />
            <span className="text-[10px] font-mono text-muted-foreground max-w-20 truncate">{store.activeLayer.name}</span>
            {store.activeLayer.locked && <Lock className="h-2.5 w-2.5 text-destructive" />}
          </div>
        )}

        {/* View controls */}
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={() => store.setZoom(store.zoom * 1.2)} className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-tool-hover hover:text-foreground">
                <ZoomIn className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Zoom in</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={() => store.setZoom(store.zoom * 0.8)} className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-tool-hover hover:text-foreground">
                <ZoomOut className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Zoom out</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={handleResetView} className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-tool-hover hover:text-foreground">
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Fit to view</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Project Properties Modal */}
      {showProjectProps && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-96 rounded-lg border border-border bg-popover p-6 shadow-xl">
            <h2 className="text-sm font-semibold text-foreground mb-4">Project Properties</h2>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Project Name</label>
                <input
                  type="text" value={projProps.projectName}
                  onChange={(e) => setProjProps(p => ({ ...p, projectName: e.target.value }))}
                  className="w-full rounded bg-secondary px-3 py-1.5 text-xs text-foreground outline-none border border-border focus:border-primary"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Brand</label>
                <input
                  type="text" value={projProps.brand}
                  onChange={(e) => setProjProps(p => ({ ...p, brand: e.target.value }))}
                  className="w-full rounded bg-secondary px-3 py-1.5 text-xs text-foreground outline-none border border-border focus:border-primary"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">ECU Name</label>
                <input
                  type="text" value={projProps.ecuName}
                  onChange={(e) => setProjProps(p => ({ ...p, ecuName: e.target.value }))}
                  className="w-full rounded bg-secondary px-3 py-1.5 text-xs text-foreground outline-none border border-border focus:border-primary"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">PCB Version</label>
                <input
                  type="text" value={projProps.pcbVersion}
                  onChange={(e) => setProjProps(p => ({ ...p, pcbVersion: e.target.value }))}
                  className="w-full rounded bg-secondary px-3 py-1.5 text-xs text-foreground outline-none border border-border focus:border-primary"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Comment</label>
                <textarea
                  value={projProps.comment}
                  onChange={(e) => setProjProps(p => ({ ...p, comment: e.target.value }))}
                  rows={3}
                  className="w-full rounded bg-secondary px-3 py-1.5 text-xs text-foreground outline-none border border-border focus:border-primary resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setShowProjectProps(false)}
                className="rounded px-4 py-1.5 text-xs text-muted-foreground hover:bg-secondary transition-colors"
              >Cancel</button>
              <button
                onClick={handleSaveProjectProps}
                className="rounded bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Preferences</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Palette Colors (8)</label>
              <div className="grid grid-cols-8 gap-1">
                {prefColors.map((color, i) => (
                  <div key={i} className="relative">
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => {
                        const newColors = [...prefColors]
                        newColors[i] = e.target.value
                        setPrefColors(newColors)
                      }}
                      className="w-7 h-7 rounded cursor-pointer border border-border"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Max Text Size: {prefMaxTextSize}px</label>
              <Slider
                min={24} max={200} step={4}
                value={[prefMaxTextSize]}
                onValueChange={([v]) => setPrefMaxTextSize(v)}
              />
            </div>
            <div className="text-[10px] text-muted-foreground">
              Note: Palette colors will apply to new layers. Max text size affects the font size slider range.
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-2">
            <button
              onClick={() => setShowPreferences(false)}
              className="rounded px-4 py-1.5 text-xs text-muted-foreground hover:bg-secondary transition-colors"
            >Close</button>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  )
}
